import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useAuth } from "@/hooks/use-auth";
import { useIsMobile } from "@/hooks/use-mobile";
import { supabase } from "@/integrations/supabase/client";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Sheet, SheetContent } from "@/components/ui/sheet";
import { Clock, CheckCircle, AlertCircle, Loader2, Menu, X } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { ClientProjects } from "@/components/client-portal/ClientProjects";
import { ClientAnalytics } from "@/components/client-portal/ClientAnalytics";
import { ServiceRequestForm } from "@/components/client-portal/ServiceRequestForm";
import { ClientSidebar } from "@/components/client-portal/ClientSidebar";
import { ClientChat } from "@/components/client-portal/ClientChat";
import { ClientNotifications } from "@/components/client-portal/ClientNotifications";
import { ClientSettings } from "@/components/client-portal/ClientSettings";
import { ClientInvoices } from "@/components/client-portal/ClientInvoices";
import { ClientPayments } from "@/components/client-portal/ClientPayments";
import { EnhancedAnalytics } from "@/components/client-portal/EnhancedAnalytics";
import { DocumentLibrary } from "@/components/client-portal/DocumentLibrary";
import { MeetingScheduler } from "@/components/client-portal/MeetingScheduler";
import { KnowledgeBase } from "@/components/client-portal/KnowledgeBase";
import { AIAssistant } from "@/components/ai-assistant/AIAssistant";
import { Button } from "@/components/ui/button";
interface ServiceRequest {
  id: string;
  service_type: string;
  business_name: string;
  description: string;
  status: string;
  budget_range: string;
  preferred_start_date: string;
  created_at: string;
}
const ClientPortal = () => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const { toast } = useToast();
  const isMobile = useIsMobile();
  const [searchParams, setSearchParams] = useSearchParams();
  const [requests, setRequests] = useState<ServiceRequest[]>([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState(searchParams.get("tab") || "overview");
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const [isCollapsed, setIsCollapsed] = useState(false);
  useEffect(() => {
    if (!user) {
      navigate("/client-login");
      return;
    }
    fetchRequests();
  }, [user, navigate]);
  const fetchRequests = async () => {
    try {
      const {
        data,
        error
      } = await supabase.from("service_requests").select("*").order("created_at", {
        ascending: false
      });
      if (error) throw error;
      setRequests(data || []);
    } catch (error: any) {
      toast({
        title: "Error",
        description: error.message,
        variant: "destructive"
      });
    } finally {
      setLoading(false);
    }
  };
  const getStatusIcon = (status: string) => {
    switch (status) {
      case "pending":
        return <Clock className="h-4 w-4" />;
      case "in_progress":
        return <Loader2 className="h-4 w-4 animate-spin" />;
      case "completed":
        return <CheckCircle className="h-4 w-4" />;
      default:
        return <AlertCircle className="h-4 w-4" />;
    }
  };
  const getStatusColor = (status: string) => {
    switch (status) {
      case "pending":
        return "bg-warning/10 text-warning border-warning/20";
      case "in_progress":
        return "bg-primary/10 text-primary border-primary/20";
      case "completed":
        return "bg-success/10 text-success border-success/20";
      default:
        return "bg-muted text-muted-foreground border-border";
    }
  };
  const handleSignOut = async () => {
    await signOut();
    navigate("/");
  };
  const handleTabChange = (tab: string) => {
    setActiveTab(tab);
    setSearchParams({
      tab
    });
    if (isMobile) setSidebarOpen(false);
  };
  useEffect(() => {
    const tab = searchParams.get("tab");
    if (tab) setActiveTab(tab);
  }, [searchParams]);
  const getPageTitle = () => {
    switch (activeTab) {
      case "overview":
        return "Dashboard Overview";
      case "projects":
        return "My Projects";
      case "requests":
        return "Service Requests";
      case "new-request":
        return "New Service Request";
      case "documents":
        return "Document Library";
      case "meetings":
        return "Meetings";
      case "invoices":
        return "Invoices";
      case "payments":
        return "Payment History";
      case "chat":
        return "Support Chat";
      case "notifications":
        return "Notifications";
      case "settings":
        return "Settings";
      case "analytics":
        return "Analytics & Insights";
      case "knowledge":
        return "Knowledge Base";
      default:
        return "Dashboard";
    }
  };
  const getPageDescription = () => {
    switch (activeTab) {
      case "overview":
        return "View your account summary and recent activity";
      case "projects":
        return "Track progress on your active and completed projects";
      case "requests":
        return "Manage and monitor all your service requests";
      case "new-request":
        return "Submit a new project request to our team";
      case "documents":
        return "Access and manage your project documents and files";
      case "meetings":
        return "Schedule and manage meetings with the team";
      case "invoices":
        return "View and download your invoices";
      case "payments":
        return "Track all your payments and transactions";
      case "chat":
        return "Chat with our support team";
      case "notifications":
        return "View all your notifications and updates";
      case "settings":
        return "Manage your account preferences";
      case "analytics":
        return "Analyze your project performance and ROI";
      case "knowledge":
        return "Browse helpful guides and resources";
      default:
        return "";
    }
  };
  
  return (
    <>
    <div className="min-h-screen flex w-full bg-gradient-to-br from-background via-muted/30 to-background">
      {/* Desktop Sidebar */}
      {!isMobile && (
        <ClientSidebar 
          onSignOut={handleSignOut} 
          activeTab={activeTab} 
          onTabChange={handleTabChange} 
          isCollapsed={isCollapsed} 
          onToggleCollapse={() => setIsCollapsed(!isCollapsed)} 
        />
      )}

      {/* Mobile Sheet Sidebar */}
      {isMobile && (
        <Sheet open={sidebarOpen} onOpenChange={setSidebarOpen}>
          <SheetContent side="left" className="w-[280px] p-0">
            <ClientSidebar 
              onSignOut={handleSignOut} 
              activeTab={activeTab} 
              onTabChange={handleTabChange} 
              isCollapsed={false} 
              onToggleCollapse={() => setSidebarOpen(false)} 
            />
          </SheetContent>
        </Sheet>
      )}

      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        !isMobile ? (isCollapsed ? 'ml-16' : 'ml-64') : ''
      }`}>
        {/* Header */}
        <header className="sticky top-0 z-40 border-b border-border/50 glass-card backdrop-blur-xl">
          <div className="flex items-center gap-2 sm:gap-4 p-3 sm:p-4 md:p-6 lg:p-8">
            {isMobile && (
              <Button
                variant="ghost"
                size="icon"
                className="touch-manipulation shrink-0"
                onClick={() => setSidebarOpen(true)}
              >
                <Menu className="h-5 w-5" />
              </Button>
            )}
            <div className="flex-1 min-w-0">
              <h1 className="text-lg sm:text-2xl md:text-3xl font-bold text-gradient truncate">{getPageTitle()}</h1>
              <p className="text-xs sm:text-sm text-muted-foreground truncate mt-0.5 sm:mt-1">{getPageDescription()}</p>
            </div>
          </div>
        </header>

        {/* Content Area */}
        <main className="flex-1 p-3 sm:p-4 md:p-6 lg:p-8 overflow-x-hidden">
          <div className="mx-auto w-full max-w-[1400px] animate-fade-in">
              {activeTab === "overview" && <div className="space-y-4 sm:space-y-6 animate-fade-in">
                  <ClientAnalytics />
                  
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-4 sm:gap-6">
                    <Card className="glass-card p-4 sm:p-6">
                      <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Recent Activity</h3>
                      <p className="text-sm text-muted-foreground">Your latest updates will appear here</p>
                    </Card>
                    
                    <Card className="glass-card p-4 sm:p-6">
                      <h3 className="text-base sm:text-lg font-semibold mb-3 sm:mb-4">Quick Actions</h3>
                      <div className="space-y-2">
                        <Button variant="outline" className="w-full justify-start touch-manipulation h-11" onClick={() => handleTabChange("new-request")}>
                          Submit New Request
                        </Button>
                        <Button variant="outline" className="w-full justify-start touch-manipulation h-11" onClick={() => handleTabChange("projects")}>
                          View Projects
                        </Button>
                      </div>
                    </Card>
                  </div>
                </div>}

              {activeTab === "projects" && <div className="animate-fade-in">
                  <ClientProjects />
                </div>}

              {activeTab === "new-request" && <div className="animate-fade-in">
                  <ServiceRequestForm onSuccess={() => {
              fetchRequests();
              toast({
                title: "Success",
                description: "Your request has been submitted successfully!"
              });
            }} />
                </div>}

              {activeTab === "chat" && <div className="animate-fade-in">
                  <ClientChat />
                </div>}

              {activeTab === "invoices" && <div className="animate-fade-in">
                  <ClientInvoices />
                </div>}

              {activeTab === "payments" && <div className="animate-fade-in">
                  <ClientPayments />
                </div>}

              {activeTab === "analytics" && <div className="animate-fade-in">
                  <EnhancedAnalytics />
                </div>}

              {activeTab === "documents" && <div className="animate-fade-in">
                  <DocumentLibrary />
                </div>}

              {activeTab === "meetings" && <div className="animate-fade-in">
                  <MeetingScheduler />
                </div>}

              {activeTab === "knowledge" && <div className="animate-fade-in">
                  <KnowledgeBase />
                </div>}

              {activeTab === "notifications" && <div className="animate-fade-in">
                  <ClientNotifications />
                </div>}

              {activeTab === "settings" && <div className="animate-fade-in">
                  <ClientSettings />
                </div>}

              {activeTab === "analytics" && <div className="animate-fade-in">
                  <ClientAnalytics />
                </div>}

              {activeTab === "requests" && <div className="animate-fade-in">
                  {loading ? <div className="text-center py-12">
                      <Loader2 className="h-12 w-12 animate-spin text-primary mx-auto" />
                      <p className="text-sm sm:text-base text-muted-foreground mt-4">Loading your requests...</p>
                    </div> : requests.length === 0 ? <Card className="glass-card p-6 sm:p-12 text-center">
                      <AlertCircle className="h-12 w-12 sm:h-16 sm:w-16 text-muted-foreground mx-auto mb-4" />
                      <h3 className="text-lg sm:text-xl font-semibold mb-2">No Requests Yet</h3>
                      <p className="text-sm sm:text-base text-muted-foreground mb-6">
                        You haven't submitted any service requests yet.
                      </p>
                      <Button className="btn-primary touch-manipulation h-11" onClick={() => handleTabChange("new-request")}>
                        Submit Your First Request
                      </Button>
                    </Card> : <div className="grid gap-4 sm:gap-6">
                      {requests.map(request => <Card key={request.id} className="glass-card-hover p-4 sm:p-6">
                          <div className="flex flex-col sm:flex-row items-start justify-between gap-3 sm:gap-4 mb-4">
                            <div className="flex-1 min-w-0">
                              <h3 className="text-lg sm:text-xl font-semibold mb-1 truncate">{request.business_name}</h3>
                              <p className="text-xs sm:text-sm text-muted-foreground">
                                {request.service_type} • Requested{" "}
                                {new Date(request.created_at).toLocaleDateString()}
                              </p>
                            </div>
                            <Badge className={`${getStatusColor(request.status)} shrink-0`}>
                              {getStatusIcon(request.status)}
                              <span className="ml-2 capitalize">{request.status.replace("_", " ")}</span>
                            </Badge>
                          </div>
                          <p className="text-sm sm:text-base text-muted-foreground mb-4 line-clamp-3">{request.description}</p>
                          <div className="flex flex-col sm:flex-row items-start sm:items-center gap-2 sm:gap-4 text-xs sm:text-sm text-muted-foreground">
                            {request.budget_range && <span>Budget: {request.budget_range}</span>}
                            {request.preferred_start_date && <span>Start: {new Date(request.preferred_start_date).toLocaleDateString()}</span>}
                          </div>
                        </Card>)}
                    </div>}
                </div>}
            </div>
          </main>
        </div>
      </div>
      
      <AIAssistant context="client" />
    </>
  );
};

export default ClientPortal;